// FILE: ThreadAssigneesControl.logic.ts
// Purpose: Turn the user directory plus a thread's ownership into the rows the sharing popup renders, and narrow them by a search query.
// Exports: `buildThreadAssigneeRows`, `filterThreadAssigneeRows`, `summarizeThreadAssignees`, `ThreadAssigneeRow`.

import type { UserDirectoryEntry, UserId } from "@taskin-code/contracts";

export interface ThreadAssigneeRow {
  readonly userId: UserId;
  readonly displayName: string;
  readonly avatarFileName: string | null;
  readonly assigned: boolean;
  /**
   * The creator. Ownership is not a membership row, so there is nothing to
   * revoke — the menu shows them as permanently on the thread.
   */
  readonly isOwner: boolean;
  readonly isSelf: boolean;
  /** Assigned, but no longer in the directory (deleted account). */
  readonly isUnknown: boolean;
}

export interface BuildThreadAssigneeRowsInput {
  readonly directory: ReadonlyArray<UserDirectoryEntry>;
  readonly createdBy: UserId | null | undefined;
  readonly assignedUserIds: ReadonlyArray<UserId> | undefined;
  readonly currentUserId: UserId | null | undefined;
  /**
   * The signed-in user's own avatar, as held by the auth store. Used for their
   * row when the directory hasn't (yet) reflected a freshly-set picture, or
   * doesn't list them at all — the directory always keeps the caller in, but
   * a stale fetch should not show a face that is already out of date.
   */
  readonly currentUserAvatarFileName?: string | null | undefined;
}

const byDisplayName = (left: ThreadAssigneeRow, right: ThreadAssigneeRow): number =>
  left.displayName.localeCompare(right.displayName);

/**
 * Owner first, then everyone the thread is shared with, then the rest of the
 * directory — the order in which someone actually reads the list ("who has
 * this?" before "who could?").
 *
 * A user that is assigned but missing from the directory is still listed. Their
 * account is gone, but the membership row is not, and hiding it would leave a
 * grant nobody can find to remove.
 */
export function buildThreadAssigneeRows(
  input: BuildThreadAssigneeRowsInput,
): ReadonlyArray<ThreadAssigneeRow> {
  const assigned = new Set(input.assignedUserIds ?? []);
  const createdBy = input.createdBy ?? null;

  const rows: ThreadAssigneeRow[] = input.directory.map((entry) => ({
    userId: entry.id,
    displayName: entry.displayName,
    avatarFileName:
      entry.id === input.currentUserId
        ? (input.currentUserAvatarFileName ?? entry.avatarFileName)
        : entry.avatarFileName,
    assigned: assigned.has(entry.id) || entry.id === createdBy,
    isOwner: entry.id === createdBy,
    isSelf: entry.id === input.currentUserId,
    isUnknown: false,
  }));

  const known = new Set(rows.map((row) => row.userId));
  for (const userId of assigned) {
    if (known.has(userId)) continue;
    rows.push({
      userId,
      displayName: "Unknown user",
      avatarFileName:
        userId === input.currentUserId ? (input.currentUserAvatarFileName ?? null) : null,
      assigned: true,
      isOwner: userId === createdBy,
      isSelf: userId === input.currentUserId,
      isUnknown: true,
    });
  }

  const owners = rows.filter((row) => row.isOwner).toSorted(byDisplayName);
  const members = rows.filter((row) => !row.isOwner && row.assigned).toSorted(byDisplayName);
  const rest = rows.filter((row) => !row.isOwner && !row.assigned).toSorted(byDisplayName);
  return [...owners, ...members, ...rest];
}

/**
 * Narrow the rows to those whose display name contains the query.
 *
 * Case-insensitive and substring-based rather than prefix-based: people search
 * for a surname as often as a first name. The order established by
 * `buildThreadAssigneeRows` is preserved — a search reduces the list, it does
 * not re-rank it, so the owner stays on top of whatever survives.
 *
 * An empty or whitespace-only query means "no filter" rather than "no matches":
 * an empty input is the resting state of the search box, not a search for the
 * empty string.
 */
export function filterThreadAssigneeRows(
  rows: ReadonlyArray<ThreadAssigneeRow>,
  query: string,
): ReadonlyArray<ThreadAssigneeRow> {
  const normalized = query.trim().toLowerCase();
  if (normalized.length === 0) return rows;
  return rows.filter((row) => row.displayName.toLowerCase().includes(normalized));
}

/**
 * Label for the trigger: how many people are on the thread besides its owner.
 * Zero reads as "not shared" rather than "0", which would look like an error.
 */
export function summarizeThreadAssignees(
  assignedUserIds: ReadonlyArray<UserId> | undefined,
): string {
  const count = assignedUserIds?.length ?? 0;
  if (count === 0) return "Not shared";
  return count === 1 ? "Shared with 1 person" : `Shared with ${count} people`;
}
